import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { clearCachedToken } from 'src/api/requests';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import {
  isJWT,
  extractUserIdFromToken,
  waitForOrganizationClaim,
  getPayloadJWT,
} from 'src/utils/jwt';

export const OrgSwitch = () => {
  const [params] = useSearchParams();
  const orgId = params.get('orgId');
  const returnTo = params.get('returnTo') || '/settings';
  const navigate = useNavigate();
  const auth = getAuth();
  const { patch } = useAuthedRequest();
  const { updateEnvironment } = useEnvironment();

  useEffect(() => {
    async function run() {
      const user = auth.currentUser;
      if (!orgId || !user) {
        navigate(returnTo, { replace: true });
        return;
      }

      const token = await user.getIdToken();
      if (!token || !isJWT(token)) {
        navigate('/login', { replace: true });
        return;
      }
      const userId = extractUserIdFromToken(token);
      if (!userId) {
        navigate(returnTo, { replace: true });
        return;
      }

      try {
        await patch(`/users/${userId}/organizations/${orgId}`, {});

        // Wait for 10 seconds for custom claims to include orgId
        await waitForOrganizationClaim({
          user,
          targetOrgId: orgId,
          maxAttempts: 100,
          delayMs: 250,
        });

        // Force request layer to fetch fresh token (invalidate cached token)
        clearCachedToken();

        // Decode environment from refreshed token and update context
        const refreshedToken = await user.getIdToken();
        const refreshedClaims = getPayloadJWT(refreshedToken);
        const newEnvId = refreshedClaims?.organization?.environments?.[0]?.id;
        if (newEnvId) {
          updateEnvironment({ userId, envId: newEnvId });
        }
      } catch (error) {
        console.error('Error switching organization', error);
      } finally {
        navigate(returnTo, { replace: true });
      }
    }
    run();
  }, [orgId, returnTo, auth.currentUser, navigate, patch, updateEnvironment]);

  return (
    <div className="flex flex-col items-center justify-start min-h-screen w-full p-8 bg-neutral-0">
      <div className="text-center">
        <div className="mb-4 text-lg font-bold">Switching organization…</div>
        <div className="animate-pulse">Please wait</div>
      </div>
    </div>
  );
};
